import { useEffect, useState, type FormEvent } from 'react';
import type { BlobSettings, BlobTestResult } from '@ia/shared';
import { api, errorMessage } from '../api';
import { Alert, Field, Spinner, When } from '../components/ui';

type KeyMode = 'keep' | 'replace' | 'remove';

export function BlobSettingsPage() {
  const [settings, setSettings] = useState<BlobSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [container, setContainer] = useState('');
  const [prefix, setPrefix] = useState('');
  const [workerContainer, setWorkerContainer] = useState('');
  const [keyMode, setKeyMode] = useState<KeyMode>('keep');
  const [connectionString, setConnectionString] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<BlobTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const hydrate = (s: BlobSettings) => {
    setSettings(s);
    setContainer(s.container);
    setPrefix(s.prefix);
    setWorkerContainer(s.workerContainer);
    setKeyMode(s.source === 'settings' ? 'keep' : 'replace');
    setConnectionString('');
  };

  useEffect(() => {
    api.blobSettings().then(hydrate, (e) => setLoadError(errorMessage(e)));
  }, []);

  const dirty =
    !!settings &&
    (container.trim() !== settings.container ||
      prefix.trim().replace(/^\/+|\/+$/g, '') !== settings.prefix ||
      workerContainer.trim() !== settings.workerContainer ||
      (keyMode === 'replace' && connectionString.length > 0) ||
      keyMode === 'remove');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    if (!container.trim() || !workerContainer.trim()) return setSaveError('Enter both container names.');
    const body: Parameters<typeof api.updateBlobSettings>[0] = { container: container.trim(), prefix: prefix.trim(), workerContainer: workerContainer.trim() };
    if (keyMode === 'replace' && connectionString) {
      if (!/AccountName=/i.test(connectionString)) return setSaveError('That does not look like a storage connection string (no AccountName=).');
      body.connectionString = connectionString.trim();
    } else if (keyMode === 'remove') {
      body.connectionString = null;
    }
    setSaving(true);
    try {
      hydrate(await api.updateBlobSettings(body));
      setSaved(true);
      setTest(null);
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTest(null);
    setTestError(null);
    try {
      setTest(await api.testBlob());
    } catch (e) {
      setTestError(errorMessage(e));
    } finally {
      setTesting(false);
    }
  };

  if (loadError)
    return (
      <div className="page">
        <h1>Blob storage</h1>
        <Alert>{loadError}</Alert>
      </div>
    );
  if (!settings)
    return (
      <div className="page">
        <Spinner />
      </div>
    );

  const sourceText =
    settings.source === 'settings'
      ? `Using the connection string saved here (account ${settings.accountName ?? '?'}).`
      : settings.source === 'env'
        ? `Using the server's BLOB_CONNECTION_STRING environment variable (account ${settings.accountName ?? '?'}). Saving one here overrides it.`
        : 'No connection string anywhere: the Process button is disabled until one is saved here or set on the server.';

  return (
    <div className="page narrow">
      <div className="page-head">
        <h1>Settings · Blob storage</h1>
      </div>
      <Alert kind={settings.source === 'none' ? 'warn' : 'info'}>{sourceText}</Alert>

      <form className="card form-card" onSubmit={submit}>
        <Field label="Container" hint="data-sync goes straight to the DataHub worker; data-import goes through the Python ETL first.">
          {(id) => <input id={id} value={container} onChange={(e) => setContainer(e.target.value)} maxLength={100} spellCheck={false} required />}
        </Field>
        <Field label="Folder (prefix)" hint='Zips land at <folder>/<uploadId>/<uploadId>.zip. "To Be Processed" for data-sync; leave empty for data-import.'>
          {(id) => <input id={id} value={prefix} onChange={(e) => setPrefix(e.target.value)} maxLength={200} spellCheck={false} />}
        </Field>
        <Field
          label="Worker container"
          hint="Where the DataHub worker moves a finished zip to Processed/ or Failed/ (data-sync, even when uploads go to data-import through the ETL). Checked to mark a batch Processed."
        >
          {(id) => <input id={id} value={workerContainer} onChange={(e) => setWorkerContainer(e.target.value)} maxLength={100} spellCheck={false} required />}
        </Field>

        <div className="field">
          <span className="label">Connection string</span>
          {settings.source === 'settings' && keyMode === 'keep' && (
            <div className="key-row">
              <code className="key-hint">AccountName={settings.accountName ?? '?'}; AccountKey=…</code>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('replace')}>
                Replace
              </button>
              <button type="button" className="btn btn-sm btn-danger-ghost" onClick={() => setKeyMode('remove')}>
                Remove
              </button>
            </div>
          )}
          {keyMode === 'remove' && (
            <div className="key-row">
              <span className="text-failed">The saved connection string will be removed when you save; the server environment is used instead, if set.</span>
              <button type="button" className="btn btn-sm" onClick={() => setKeyMode('keep')}>
                Undo
              </button>
            </div>
          )}
          {keyMode === 'replace' && (
            <div className="key-row">
              <input
                type="password"
                aria-label="Storage account connection string"
                placeholder="DefaultEndpointsProtocol=https;AccountName=…;AccountKey=…;EndpointSuffix=core.windows.net"
                value={connectionString}
                onChange={(e) => setConnectionString(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                className="grow"
              />
              {settings.source === 'settings' && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setConnectionString('');
                    setKeyMode('keep');
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
          <div className="hint">Write-only: stored encrypted on the server and never shown again. Leave empty to keep the current one.</div>
        </div>

        {saveError && <Alert>{saveError}</Alert>}
        {saved && !dirty && <Alert kind="ok">Settings saved.</Alert>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void runTest()}
            disabled={testing || !settings.hasConnectionString || dirty}
            title={dirty ? 'Save your changes first' : !settings.hasConnectionString ? 'Add a connection string first' : undefined}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <span className="spacer" />
          <span className="muted small">
            Last updated {settings.updatedAt ? <When iso={settings.updatedAt} inline /> : 'never'}
            {settings.updatedBy && <> by {settings.updatedBy}</>}
          </span>
        </div>

        {testError && <Alert>{testError}</Alert>}
        {test &&
          (test.ok ? (
            <Alert kind="ok">
              Connection OK — account <code>{test.accountName}</code>, container <code>{test.container}</code> found.
            </Alert>
          ) : (
            <Alert>
              Connection failed{test.accountName ? <> for account <code>{test.accountName}</code></> : null}: {test.error ?? 'unknown error'}
            </Alert>
          ))}
      </form>
    </div>
  );
}
